import { snakeCase } from 'lodash';
import { memo, useCallback, useMemo, useState } from 'react';
import TradeInput from 'src/components/Inputs/TradeInput';
import PriceInputMidButton from 'src/components/TradeForm/PriceInput/PriceInputMidButton';
import { BN, MarketType, PRICE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { MarketId } from '@velocity-exchange/common';
import {
	inputFieldConfig,
	marketPrecisionFromSizes,
} from '@velocity-exchange/common/format';
import { numberFormatPropsFromConfig } from 'src/components/primitives/Input/numberFormatProps';
import {
	getTickSizeDecimalPlaces,
	useTickSizeForMarket,
} from 'src/hooks/useGetTickSizeForMarket';
import { getEffectivePriceDecimals } from 'src/constants/marketDecimalConfig';

/**
 * The mask settings and the outbound step the price field runs on, for a
 * market's tick size.
 */
export function priceInputFieldProps(baseAssetSymbol: string, tickSize: BN) {
	const config = inputFieldConfig('price', {
		market: marketPrecisionFromSizes({
			tickSize,
			tickPrecisionExp: PRICE_PRECISION_EXP,
			// A price field reads only the tick lattice. The size lattice is the
			// size field's, so the tick stands in for it here.
			stepSize: tickSize,
			stepPrecisionExp: PRICE_PRECISION_EXP,
		}),
		// The market config can only tighten the on-chain tick, never loosen it.
		overrides: {
			maxFractionDigits: getEffectivePriceDecimals(
				baseAssetSymbol,
				getTickSizeDecimalPlaces(tickSize)
			),
		},
	});

	return {
		numberFormatProps: numberFormatPropsFromConfig(config),
		stepSize: config.step,
	};
}

interface Props {
	label: string;
	value: string;
	marketIndex: number;
	marketType: MarketType;
	handleChange: (value: string) => void;
	handleBlur?: (value: string) => void;
	showMidButton?: boolean;
	disabled?: boolean;
	className?: string;
	placeholder?: string;
	allowNegative?: boolean;
	baseAssetSymbol: string;
	dataTestId?: string;
}

const PriceInput = memo(function PriceInputMemo({
	label,
	value,
	marketIndex,
	marketType,
	handleChange,
	handleBlur,
	showMidButton,
	disabled,
	className,
	placeholder,
	allowNegative,
	baseAssetSymbol,
	dataTestId,
}: Props) {
	const marketId = new MarketId(marketIndex, marketType);
	const tickSize = useTickSizeForMarket(marketId);

	const { numberFormatProps, stepSize } = useMemo(
		() => priceInputFieldProps(baseAssetSymbol, tickSize),
		[baseAssetSymbol, tickSize]
	);

	const [showErrorMessage, setShowErrorMessage] = useState(false);

	const priceError = useMemo(() => {
		if (!value || (!allowNegative && parseFloat(value) === 0)) {
			return 'Please input a valid price';
		}
		return null;
	}, [value, allowNegative]);

	const onBlur = useCallback(() => {
		setShowErrorMessage(true);
		handleBlur?.(value);
	}, [handleBlur, value]);

	const onFocus = useCallback(() => {
		setShowErrorMessage(false);
	}, []);

	return (
		<TradeInput
			id={`${snakeCase(label)}-input`}
			value={value}
			onChange={handleChange}
			label={`${label} (USD)`}
			onBlur={onBlur}
			onFocus={onFocus}
			suffix={
				showMidButton ? (
					<PriceInputMidButton
						marketIndex={marketIndex}
						handleChange={handleChange}
						marketType={marketType}
						baseAssetSymbol={baseAssetSymbol}
					/>
				) : undefined
			}
			disabled={disabled}
			inputClassName={className}
			placeholder={placeholder}
			allowNegative={allowNegative}
			numberFormatProps={numberFormatProps}
			stepSize={stepSize}
			error={showErrorMessage && priceError}
			dataTestId={dataTestId}
		/>
	);
});

export default PriceInput;
